import React, { useState, useEffect, Fragment } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
// utils
import { t } from '@insight/toolkit-utils';
import { getVariants } from '../../utils/getProductVariants';
import { makeProductDetailURL } from '../../PDPModal/PDPHelpers';
// components
import Button from '../../Button/Button';
import Tooltip from '../../Tooltip/Tooltip';

const VARIANT_LIMIT = 5;
const COLOR_REGEX = /[0-9A-Fa-f]{6}/g;
const COLOR_VARIANT_CODE = 'color_variant';

const SpecificationsVariants = (props) => {
  const [showMore, setShowMore] = useState(false);
  const {
    variants: productVariants,
    sendTracking,
    limit,
    generateProductDetailUrl,
  } = props || {};
  const variants =
    productVariants?.length > 0 ? getVariants(productVariants) : [];
  const createProductDetailUrl =
    generateProductDetailUrl || makeProductDetailURL;
  const availableOptionsLabel = t('See available options');

  // Send variant tracking on load
  useEffect(() => {
    if (sendTracking) {
      sendTracking?.({ variant: true });
    }
  }, []);

  // Renders color variants
  const renderColors = (options, category) => {
    const renderedColors = () => {
      if (!options.length) return null;
      return options.map((option, index) => {
        const disabled = !option.selectable && !option.selected;
        return (
          <>
            <Tooltip
              key={index}
              id={`color_${index}`}
              className="c-product-specifications__tooltip"
              content={
                <>
                  <span className="tooltip-label">{option.unit?.label} </span>
                  {disabled ? <span>- {availableOptionsLabel}</span> : null}
                </>
              }
            >
              <div className={cn('c-colors__container', { disabled })}>
                <Button
                  aria-label={option.unit?.label}
                  className={cn('c-colors__color', {
                    selected: option.selected,
                    disabled,
                  })}
                  onClick={() =>
                    sendTracking({
                      variantCategory: category,
                      variantSubCategory: option.unit?.label,
                    })
                  }
                  href={createProductDetailUrl({
                    materialId: option.materialId,
                    mfrName: option.manufacturerName,
                    manufacturerId: option.manufacturerPartNumber,
                    description: option.description,
                  })}
                  style={{ backgroundColor: `#${option.value}` }}
                />
              </div>
            </Tooltip>
            <div className="u-hide-visually" id={`color_${index}`} />
          </>
        );
      });
    };
    return (
      <div className="c-colors">
        <div className="c-colors__options" data-testid="c-colors__options">
          {renderedColors()}
        </div>
      </div>
    );
  };

  // Renders both measurement and optional variants
  const renderOptions = (options, category) => {
    const renderOptions = () => {
      if (!options.length) return null;

      // Set limit to accessories length or limit to 5
      const length = showMore ? options.length : limit;

      const optionsList = options.slice(0, length);
      return optionsList.map((option, index) => {
        const disabled = !option.selectable && !option.selected;
        const displayValue = option?.measurement
          ? `${option.value} ${option?.unit?.symbol}`
          : option.unit?.label;
        return (
          <>
            <Tooltip
              key={index}
              id={`variant_${index}`}
              className="c-product-specifications__tooltip"
              content={
                disabled && (
                  <>
                    <span className="tooltip-label">{displayValue} </span>
                    <span>- {availableOptionsLabel}</span>
                  </>
                )
              }
            >
              <Button
                aria-label={displayValue}
                className={cn('c-switch-options__option', {
                  selected: option.selected,
                  disabled,
                })}
                onClick={() =>
                  sendTracking({
                    variantCategory: category,
                    variantSubCategory: displayValue,
                  })
                }
                href={createProductDetailUrl({
                  materialId: option.materialId,
                  mfrName: option.manufacturerName,
                  manufacturerId: option.manufacturerPartNumber,
                  description: option.description,
                })}
              >
                <span className="c-switch-options__option__value">
                  {displayValue}
                </span>
              </Button>
            </Tooltip>
            <div className="u-hide-visually" id={`variant_${index}`} />
          </>
        );
      });
    };

    return (
      <div className="c-switch-options" data-testid="c-switch-options">
        {renderOptions()}
        {options.length > limit ? (
          <div className="c-switch-options__option__show-more">
            {showMore ? (
              <Button
                className="c-switch-options__option__show-more__link"
                color="inline-link"
                onClick={() => setShowMore(false)}
              >
                {t('Less')}
              </Button>
            ) : (
              <Button
                className="c-switch-options__option__show-more__link"
                color="inline-link"
                onClick={() => setShowMore(true)}
              >
                {t('More')}
              </Button>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const renderVariantOptions = (value, options, category) => {
    const isColor = COLOR_REGEX.test(value);
    const optionsArray = [...options.values()];
    if (isColor) return renderColors(optionsArray, category);
    return renderOptions(optionsArray, category);
  };

  const renderVariants = () =>
    // Sort variants by default order moving color variant first
    variants
      ?.sort((a, b) =>
        a.code === COLOR_VARIANT_CODE
          ? -1
          : b.code === COLOR_VARIANT_CODE
            ? 1
            : 0
      )
      .map(({ label, value, options, unit, measurement }, index) => (
        <section
          key={index}
          className="c-product-specifications__variants__variant"
        >
          <header className="c-product-specifications__variants__variant__header">
            <span className="c-product-specifications__variants__variant__header__key">
              {label}:
            </span>
            <span className="c-product-specifications__variants__variant__header__value">
              <span>{measurement ? value : unit?.label} </span>
              {measurement ? (
                <span className="c-product-specifications__variants__variant__header__uom">
                  {unit?.symbol}
                </span>
              ) : null}
            </span>
          </header>
          {renderVariantOptions(value, options, label)}
        </section>
      ));

  if (!variants?.length) return null;
  return (
    <section
      className="c-product-specifications__variants"
      data-testid="c-product-specifications__variants"
    >
      {renderVariants()}
    </section>
  );
};

export default SpecificationsVariants;

SpecificationsVariants.propTypes = {
  variants: PropTypes.arrayOf(PropTypes.shape({})),
  sendTracking: PropTypes.func,
  limit: PropTypes.number,
  generateProductDetailUrl: PropTypes.func,
};

SpecificationsVariants.defaultProps = {
  variants: [],
  sendTracking: () => {},
  limit: VARIANT_LIMIT,
  generateProductDetailUrl: null,
};
